/**
 * Language of the service (owner 12.9.2026: the picker belongs on the sign-in screen).
 *
 * The market is chosen before the account exists, so the choice lives in the browser and travels with the player into
 * every room they create: a room carries its language, and public Meet/Flirt matchmaking already pools by it
 * (`roomType:language:region:campaign`). English is the only language whose UI and content exist today; the rest are
 * listed because the choice itself is the market signal we need — the server counts them, with no account attached, so
 * the order in which languages are actually built is decided by demand rather than by guesswork.
 *
 * A language is `ready` only when BOTH its interface strings and an authored content deck exist for it. Line questions
 * are cultural, not translatable — money, dating and family norms differ per market — so a deck is written, never
 * machine-translated, and a half-built language is never offered as if it worked.
 */
import { signal } from '@preact/signals';

export type Lang = { code: string; native: string; english: string; ready: boolean };

/** The owner's list (12.9.2026) plus the markets that carry the same product elsewhere. Order = how they are shown. */
export const LANGS: Lang[] = [
  { code: 'en', native: 'English', english: 'English', ready: true },
  { code: 'es', native: 'Español', english: 'Spanish', ready: false },
  { code: 'pt-BR', native: 'Português (Brasil)', english: 'Portuguese (Brazil)', ready: false },
  { code: 'de', native: 'Deutsch', english: 'German', ready: false },
  { code: 'fr', native: 'Français', english: 'French', ready: false },
  { code: 'it', native: 'Italiano', english: 'Italian', ready: false },
  { code: 'ja', native: '日本語', english: 'Japanese', ready: false },
  { code: 'ko', native: '한국어', english: 'Korean', ready: false },
  { code: 'zh', native: '中文', english: 'Chinese', ready: false },
  { code: 'tr', native: 'Türkçe', english: 'Turkish', ready: false },
  { code: 'id', native: 'Bahasa Indonesia', english: 'Indonesian', ready: false },
  { code: 'pl', native: 'Polski', english: 'Polish', ready: false },
];
const KEY = 'snw.lang';
export const langOf = (code: string): Lang => LANGS.find((l) => l.code === code) ?? LANGS[0]!;

/** The browser's own preference, matched to what we offer (`pt-BR` before `pt`, `zh-Hans` → `zh`). */
function detect(): string {
  const prefs = typeof navigator === 'undefined' ? [] : [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const p of prefs) {
    const exact = LANGS.find((l) => l.code.toLowerCase() === p.toLowerCase()); if (exact) return exact.code;
    const base = p.split('-')[0]!.toLowerCase();
    const hit = LANGS.find((l) => l.code.toLowerCase() === base || l.code.toLowerCase().startsWith(`${base}-`)); if (hit) return hit.code;
  }
  return 'en';
}
const stored = (() => { try { return localStorage.getItem(KEY) ?? ''; } catch { return ''; } })();
/** The player's chosen market. Rooms are created in it; the interface falls back to English until that language ships. */
export const lang = signal<string>(LANGS.some((l) => l.code === stored) ? stored : detect());
/** What the interface actually renders in — English until the chosen language's strings exist. */
export const uiLang = () => (langOf(lang.value).ready ? lang.value : 'en');

export function setLang(code: string): void {
  const l = langOf(code); if (l.code === lang.value) return;
  lang.value = l.code;
  try { localStorage.setItem(KEY, l.code); } catch { /* private mode: the choice lasts for this visit */ }
  document.documentElement.lang = l.ready ? l.code : 'en';
  // Aggregate demand only: which market was asked for, never who asked. Failure here must never block signing in.
  void fetch('/api/telemetry/language', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code: l.code }), keepalive: true }).catch(() => {});
}
